import { useEffect, useMemo, useRef, useState } from 'react';
import { subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { getPhotoUrl, listMyAttendance, type MyAttendanceRow } from '../../api/attendance';
import { listMisCanjesDesde, type CanjeRow } from '../../api/canjes';
import { listMisVisitas, type VisitaPropiaRow } from '../../api/levantamiento';
import { etiquetaEstadoVisita, tituloDePuesto } from '../../domain/levantamiento';
import { FLAG_LABEL } from '../../domain/flags';

const LIMA_TZ = 'America/Lima';

/** El texto sale de `FLAG_LABEL` (única fuente); aquí solo vive el color. */
const FLAG_CLASS: Record<string, string> = {
  tardanza: 'bg-amber-100 text-amber-800',
  salida_temprana: 'bg-amber-100 text-amber-800',
  sin_gps: 'bg-amber-100 text-amber-800',
  lejos_del_pdv: 'bg-red-100 text-red-800',
  hora_sospechosa: 'bg-red-100 text-red-800',
};

/**
 * Banderas que la persona NO ve en su historial. `foto_repetida` (0314) es un control interno de
 * Validación: 200 de las 206 del primer cálculo eran marcas que cargó a mano la oficina, así que
 * pintarla en rojo acusaba a la trabajadora de lo que no hizo; y además le diría qué se controla.
 * El coordinador la ve en Validación, con cuál es la otra marca.
 */
const OCULTAS_A_LA_PERSONA = new Set(['foto_repetida']);

const STATUS_LABEL: Record<string, string> = {
  pendiente: 'Pendiente',
  validada: 'Validada',
  observada: 'Observada',
};

/** "tu ingreso del 07/08/2026": se usa en el texto alternativo de la imagen y en el
 *  botón, para que quien no ve la foto sepa igual de cuál marcado se habla. */
function descripcionMarcado(r: MyAttendanceRow): string {
  const tipo = r.kind === 'ingreso' ? 'ingreso' : 'salida';
  return `tu ${tipo} del ${formatInTimeZone(new Date(r.device_ts), LIMA_TZ, 'dd/MM/yyyy')}`;
}

const CAJA = 'h-16 w-16 shrink-0 rounded-lg';

/** Cuántas fotos se piden sin esperar: en un celular de 375 px estas ya están a la
 *  vista al abrir la pantalla, y son las de hoy — las que la persona viene a mirar. */
const PRIMERAS_A_LA_VISTA = 3;

/**
 * Miniatura de la foto del marcado.
 *
 * La URL firmada dura 60 segundos: pedir las 30 de la lista al abrir la pantalla las
 * quema todas antes de que la persona baje hasta ellas (y gasta 30 llamadas al Storage
 * en una conexión de celular). Por eso cada miniatura pide la suya recién cuando entra
 * en pantalla, y la vista en grande pide otra fresca al abrirse.
 *
 * Si la foto no se puede mostrar —marcado viejo sin foto, firma rechazada, imagen que
 * no carga— se dibuja un recuadro que lo dice. Un ícono roto la haría dudar de su
 * propia evidencia justo cuando la está buscando para defenderse.
 */
function MiniaturaFoto({
  path,
  descripcion,
  eager,
  onAmpliar,
}: {
  path: string | null;
  descripcion: string;
  /** Los primeros marcados de la lista se piden sin esperar al observador: están en
   *  pantalla igual, y así la foto aparece aunque el navegador no entregue el aviso
   *  de visibilidad (pasa con la pestaña en segundo plano). */
  eager: boolean;
  onAmpliar: () => void;
}) {
  const [visible, setVisible] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [falla, setFalla] = useState(false);
  const caja = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!path) return;
    if (eager) {
      setVisible(true);
      return;
    }
    const el = caja.current;
    // Sin IntersectionObserver (navegador viejo) se pide igual: es preferible gastar
    // llamadas a que la persona no vea ninguna foto.
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '150px' }, // se adelanta un poco al dedo que desliza
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [path, eager]);

  useEffect(() => {
    if (!visible || !path || url !== null || falla) return;
    let alive = true;
    void getPhotoUrl(path)
      .then((u) => {
        if (alive) setUrl(u);
      })
      .catch(() => {
        if (alive) setFalla(true);
      });
    return () => {
      alive = false;
    };
  }, [visible, path, url, falla]);

  if (!path) {
    return (
      <div
        className={`${CAJA} flex items-center justify-center border border-dashed border-gray-300 bg-gray-50 p-1 text-center text-[10px] leading-tight text-gray-500`}
      >
        Sin foto
      </div>
    );
  }

  if (falla) {
    return (
      <div
        className={`${CAJA} flex items-center justify-center border border-dashed border-gray-300 bg-gray-50 p-1 text-center text-[10px] leading-tight text-gray-500`}
      >
        Foto no disponible
      </div>
    );
  }

  return (
    <div ref={caja} className={CAJA}>
      {url ? (
        <button
          type="button"
          onClick={onAmpliar}
          aria-label={`Ver en grande la foto de ${descripcion}`}
          className={`${CAJA} block overflow-hidden border border-gray-200`}
        >
          <img
            src={url}
            alt={`Foto de ${descripcion}`}
            onError={() => setFalla(true)}
            className="h-full w-full object-cover"
          />
        </button>
      ) : (
        <div className={`${CAJA} animate-pulse bg-gray-200`} aria-hidden="true" />
      )}
    </div>
  );
}

/** El color del estado de una visita. `anulado` en gris: ya no cuenta para nadie. */
const CLASE_ESTADO_VISITA: Record<string, string> = {
  medido: 'bg-emerald-100 text-emerald-800',
  no_pude: 'bg-amber-100 text-amber-800',
  anulado: 'bg-gray-200 text-gray-800',
};

/**
 * Una ficha de levantamiento en el historial.
 *
 * Sin miniatura, al revés que el marcado y el canje: las fotos de una visita cuelgan de sus campos
 * —el puesto, cada precio, cada promoción, el hallazgo— y no hay UNA que la represente. Elegir la
 * del puesto obligaría a traer las rutas de todas las visitas para pintar una sola de cada una,
 * en la pantalla que más filas tiene y en el celular de alguien que paga sus datos. Quien necesita
 * verlas las tiene en el panel, que es donde se revisan.
 */
function FilaVisita({ v }: { v: VisitaPropiaRow }) {
  return (
    <li className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-medium text-emerald-900">
          {tituloDePuesto(v)}
        </p>
        <p className="shrink-0 text-sm text-gray-500">
          {formatInTimeZone(new Date(v.registradoEn), LIMA_TZ, 'dd/MM/yyyy HH:mm')}
        </p>
      </div>
      <p className="truncate text-sm text-gray-700">{v.mercadoNombre}</p>
      <div className="mt-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
          CLASE_ESTADO_VISITA[v.estado] ?? 'bg-gray-100 text-gray-700'
        }`}>
          {etiquetaEstadoVisita(v.estado)}
        </span>
      </div>
    </li>
  );
}

export default function Historial() {
  const [rows, setRows] = useState<MyAttendanceRow[]>([]);
  const [canjes, setCanjes] = useState<CanjeRow[]>([]);
  /**
   * Las visitas de levantamiento propias.
   *
   * POR QUÉ ESTÁN ACÁ (2026-08-19). Esta pantalla mostraba solo marcados de asistencia, y un
   * auditor de levantamiento **no marca asistencia**: entraba a «Mi historial» y leía «todavía no
   * tienes marcados registrados» con veintidós visitas suyas cargadas ese mismo día. Para esa
   * persona la pantalla estaba muerta, y peor: le decía que no había registrado nada.
   */
  const [visitas, setVisitas] = useState<VisitaPropiaRow[]>([]);
  /** La lectura de visitas falló. Se dice, en vez de dejar creer que no hay ninguna. */
  const [visitasFallaron, setVisitasFallaron] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Foto abierta en grande (path + de qué marcado es), con su propia URL firmada.
  const [ampliada, setAmpliada] = useState<{ path: string; descripcion: string } | null>(null);
  const [urlGrande, setUrlGrande] = useState<string | null>(null);
  const [fallaGrande, setFallaGrande] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        // Ventana de 60 días; la consulta ya devuelve como máximo 30 marcados.
        const dateFrom = formatInTimeZone(subDays(new Date(), 60), LIMA_TZ, 'yyyy-MM-dd');
        // Los canjes y las visitas se leen APARTE y sin poder tumbar el historial: si una de esas
        // lecturas falla —una campaña sin dinámica, un permiso, la red— se muestran los marcados
        // igual. El historial de asistencia es lo que sirve para reclamar un pago; no puede
        // depender de un extra.
        //
        // La de visitas además AVISA cuando falla, y las otras dos no: para un auditor de
        // levantamiento esa lista no es un extra, es todo su historial, y una lista vacía en
        // silencio se lee exactamente igual que «no registré nada» — que es el malentendido que
        // esta pantalla acaba de costar.
        let falla = false;
        const [data, mios, misVisitas] = await Promise.all([
          listMyAttendance(dateFrom),
          listMisCanjesDesde(dateFrom).catch(() => [] as CanjeRow[]),
          listMisVisitas({ campaignId: null, desde: dateFrom, hasta: null, claveMercado: null })
            .catch(() => { falla = true; return [] as VisitaPropiaRow[]; }),
        ]);
        if (alive) {
          setRows(data); setCanjes(mios); setVisitas(misVisitas); setVisitasFallaron(falla);
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'No se pudo cargar tu historial.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /**
   * Marcados y canjes en UNA sola línea de tiempo, del más reciente al más antiguo.
   *
   * Pedido del usuario: *"me gustaría que también salgan sus registros de canje, en el orden que
   * va llegando por hora"*. Su jornada es una sola secuencia —entra, registra, registra, sale— y
   * partirla en dos listas obliga a reconstruirla mentalmente. El canje se ordena por su hora
   * real de registro, que es la que el servidor selló.
   */
  const linea = useMemo(() => {
    const deMarcados = rows.map((r) => ({
      clave: `a-${r.id}`, cuando: r.device_ts, tipo: 'marcado' as const, marcado: r,
    }));
    const deCanjes = canjes.map((c) => ({
      clave: `c-${c.id}`, cuando: c.registradoEn, tipo: 'canje' as const, canje: c,
    }));
    return [...deMarcados, ...deCanjes]
      .sort((a, b) => new Date(b.cuando).getTime() - new Date(a.cuando).getTime());
  }, [rows, canjes]);

  // La firma de la miniatura ya puede haber vencido (60 s): para verla en grande se
  // pide una nueva en el momento de abrirla.
  useEffect(() => {
    setUrlGrande(null);
    setFallaGrande(false);
    if (!ampliada) return;
    let alive = true;
    void getPhotoUrl(ampliada.path)
      .then((u) => {
        if (alive) setUrlGrande(u);
      })
      .catch(() => {
        if (alive) setFallaGrande(true);
      });
    return () => {
      alive = false;
    };
  }, [ampliada]);

  if (loading) return <p className="p-4 text-center text-gray-500">Cargando historial…</p>;
  if (error)
    return (
      <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">{error}</p>
    );

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-bold text-gray-900">Mi historial</h1>

      {/*
        DOS SECCIONES, Y LA QUE NO TIENE DATOS NO SE PINTA.
        ---------------------------------------------------
        Las dos poblaciones que usan esta pantalla no se cruzan: el recurso de asistencia marca
        entrada y salida y jamás levanta una ficha; el auditor de levantamiento llena fichas y
        jamás marca asistencia. Antes había una sola lista, la de marcados, y el auditor leía
        «todavía no tienes marcados registrados» —una frase que para él nunca iba a dejar de ser
        cierta— mientras sus visitas del día estaban cargadas en el servidor.
        Con dos secciones que se esconden solas, cada uno ve exactamente lo suyo y ninguno recibe
        un vacío que en su caso no significa nada. El encabezado de cada sección se pinta siempre
        que haya datos: es lo que dice QUÉ es esa lista cuando aparecen las dos juntas —el caso
        raro pero real de alguien que hace las dos cosas— y no estorba cuando hay una sola.
      */}
      {visitasFallaron && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          No se pudieron consultar tus levantamientos. Revisa tu señal y vuelve a entrar.
        </p>
      )}

      {/* El `!visitasFallaron` no sobra: sin él, cuando la consulta de levantamientos se cae, la
          pantalla mostraría el aviso de que no se pudo consultar y JUSTO DEBAJO la afirmación de
          que no hay nada registrado. Es la contradicción exacta que este cambio vino a quitar. */}
      {linea.length === 0 && visitas.length === 0 && !visitasFallaron ? (
        <p className="rounded-lg bg-white p-4 text-center text-sm text-gray-600">
          Todavía no tienes nada registrado: ni marcados de asistencia ni fichas de levantamiento.
        </p>
      ) : null}

      {linea.length > 0 && (
        <>
        {/* El título nombra lo que la lista REALMENTE trae. Los canjes van mezclados con los
            marcados en una sola línea de tiempo (ver `linea`), así que llamarla siempre «Mis
            marcados» escondería el canje que alguien viene a buscar; y nombrarlos siempre haría
            prometer canjes a la mayoría, que no registra ninguno. */}
        <h2 className="pt-1 text-sm font-semibold uppercase tracking-wide text-gray-500">
          {canjes.length > 0 ? 'Mis marcados y canjes' : 'Mis marcados'}
        </h2>
        <ul className="space-y-2">
          {linea.map((item, i) => item.tipo === 'canje' ? (
            <li key={item.clave} className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 shadow-sm">
              <div className="flex gap-3">
                <MiniaturaFoto
                  path={item.canje.fotoBoletaPath}
                  descripcion="Boleta del canje"
                  eager={i < PRIMERAS_A_LA_VISTA}
                  // Un canje puede no tener boleta (0036): sin foto no hay nada que ampliar.
                  onAmpliar={() => {
                    if (item.canje.fotoBoletaPath === null) return;
                    setAmpliada({
                      path: item.canje.fotoBoletaPath, descripcion: 'Boleta del canje',
                    });
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-medium text-indigo-900">
                      Canje {formatInTimeZone(new Date(item.canje.registradoEn), LIMA_TZ, 'HH:mm')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatInTimeZone(new Date(item.canje.registradoEn), LIMA_TZ, 'dd/MM/yyyy')}
                    </p>
                  </div>
                  <p className="truncate text-sm text-gray-700">
                    {item.canje.lineas
                      .flatMap((l) => l.premios.map((p) => p.prizeName))
                      .join(' + ') || 'Sin premio anotado'}
                  </p>
                  <p className="truncate text-xs text-gray-500">
                    {item.canje.pdv?.name ?? 'PDV sin nombre'} · {item.canje.lineas.map((l) => l.dynamicName).join(', ')}
                  </p>
                  {item.canje.estado === 'anulado' && (
                    <span className="mt-2 inline-block rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-800">
                      anulado
                    </span>
                  )}
                </div>
              </div>
            </li>
          ) : (
            <li key={item.clave} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
              <div className="flex gap-3">
                <MiniaturaFoto
                  path={item.marcado.photo_path}
                  descripcion={descripcionMarcado(item.marcado)}
                  eager={i < PRIMERAS_A_LA_VISTA}
                  onAmpliar={() =>
                    item.marcado.photo_path &&
                    setAmpliada({ path: item.marcado.photo_path, descripcion: descripcionMarcado(item.marcado) })
                  }
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-medium text-gray-900">
                      {item.marcado.kind === 'ingreso' ? 'Ingreso' : 'Salida'}{' '}
                      {formatInTimeZone(new Date(item.marcado.device_ts), LIMA_TZ, 'HH:mm')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatInTimeZone(new Date(item.marcado.device_ts), LIMA_TZ, 'dd/MM/yyyy')}
                    </p>
                  </div>
                  <p className="truncate text-sm text-gray-700">
                    {item.marcado.assignment.position.pdv?.name ?? 'PDV sin nombre'}
                  </p>
                  <p className="truncate text-xs text-gray-500">
                    {item.marcado.assignment.position.campaign
                      ? `${item.marcado.assignment.position.campaign.client} · ${item.marcado.assignment.position.campaign.brand}`
                      : 'Campaña sin datos'}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                      {STATUS_LABEL[item.marcado.status] ?? item.marcado.status}
                    </span>
                    {item.marcado.flags.filter((f) => !OCULTAS_A_LA_PERSONA.has(f)).map((f) => (
                      <span
                        key={f}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${FLAG_CLASS[f] ?? 'bg-gray-100 text-gray-700'}`}
                      >
                        {FLAG_LABEL[f] ?? f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
        </>
      )}

      {visitas.length > 0 && (
        <>
          <h2 className="pt-1 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Mis levantamientos
          </h2>
          <ul className="space-y-2">
            {visitas.map((v) => <FilaVisita key={v.id} v={v} />)}
          </ul>
        </>
      )}

      {ampliada && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Foto de ${ampliada.descripcion}`}
          className="fixed inset-0 z-50 flex flex-col bg-black/90 p-3"
        >
          <div className="flex items-center justify-between gap-2 pb-2">
            <p className="truncate text-sm text-white">Foto de {ampliada.descripcion}</p>
            <button
              type="button"
              onClick={() => setAmpliada(null)}
              className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white"
            >
              Cerrar
            </button>
          </div>
          <div className="flex flex-1 items-center justify-center overflow-auto">
            {fallaGrande ? (
              <p className="rounded-lg border border-dashed border-white/40 p-4 text-center text-sm text-white">
                No se pudo cargar la foto. Revisa tu señal y vuelve a intentar.
              </p>
            ) : urlGrande ? (
              <img
                src={urlGrande}
                alt={`Foto de ${ampliada.descripcion}`}
                onError={() => setFallaGrande(true)}
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <p className="text-sm text-white">Cargando foto…</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
